import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpRight,
  Brain,
  Check,
  CircleAlert,
  CornerDownLeft,
  FileText,
  Image as ImageIcon,
  Mic,
  RefreshCw,
  Search,
  Video,
  Wrench,
  X,
} from 'lucide-react';
import {
  AIModelInfo,
  AIProviderModelsResult,
  AIProviderType,
  AIUseCase,
  settingsService,
} from '../../../services/settingsService';
import { Button, Dialog, IconButton, Pill, SegmentedControl } from '../../ui';
import {
  compatibility,
  estimateUseCost,
  formatCost,
  formatPrice,
  formatRelativeTime,
  formatTokens,
  MODEL_ID_PATTERN,
  USE_CASE_META,
} from './modelFormat';
import { ProviderMeta } from './providers';

import { foldText } from '../../../utils/fuzzySearch';
type SortKey = 'cost' | 'newest' | 'context' | 'name';

interface ProviderData {
  data?: AIProviderModelsResult;
  loading: boolean;
  error?: string;
}

interface ModelPickerModalProps {
  open: boolean;
  useCase: AIUseCase;
  /** Providers with an API key, selectable in the picker */
  providers: ProviderMeta[];
  initialProvider: AIProviderType;
  /** Provider currently serving the use case, if any */
  assignedProvider?: AIProviderType;
  /** Model in effect for a provider and this use case (saved choice or default) */
  modelFor: (provider: AIProviderType) => string;
  defaultModelFor: (provider: AIProviderType) => string | undefined;
  /** Unsaved API keys typed in the form */
  typedKeyFor: (provider: AIProviderType) => string | undefined;
  /** Chosen model, or null when the admin picks the provider default */
  onSelect: (provider: AIProviderType, modelId: string | null, info: AIModelInfo | null) => void;
  onClose: () => void;
}

const SORTS: Array<{ value: SortKey; label: string }> = [
  { value: 'cost', label: 'Cheapest' },
  { value: 'newest', label: 'Newest' },
  { value: 'context', label: 'Context' },
  { value: 'name', label: 'A–Z' },
];

export const ModelPickerModal: React.FC<ModelPickerModalProps> = ({
  open,
  useCase,
  providers,
  initialProvider,
  assignedProvider,
  modelFor,
  defaultModelFor,
  typedKeyFor,
  onSelect,
  onClose,
}) => {
  const [provider, setProvider] = useState<AIProviderType>(initialProvider);
  const [byProvider, setByProvider] = useState<Partial<Record<AIProviderType, ProviderData>>>({});
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('cost');
  const [requireImage, setRequireImage] = useState(false);
  const [requireReasoning, setRequireReasoning] = useState(false);
  const [requireTools, setRequireTools] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customId, setCustomId] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Only keyboard moves scroll the list; hovering must not make it jump
  const scrollOnMove = useRef(false);
  const meta = USE_CASE_META[useCase];

  const load = useCallback(
    async (target: AIProviderType, refresh = false) => {
      setByProvider((prev) => ({ ...prev, [target]: { ...prev[target], loading: true, error: undefined } }));
      try {
        const typed = typedKeyFor(target);
        const data = await settingsService.getProviderModels(target, {
          apiKey: typed && !typed.includes('*') ? typed : undefined,
          refresh,
        });
        setByProvider((prev) => ({ ...prev, [target]: { data, loading: false } }));
      } catch (err: any) {
        setByProvider((prev) => ({
          ...prev,
          [target]: {
            ...prev[target],
            loading: false,
            error: err?.response?.data?.message || err?.message || 'Failed to load models',
          },
        }));
      }
    },
    [typedKeyFor],
  );

  useEffect(() => {
    if (open) setProvider(initialProvider);
  }, [open, initialProvider]);

  useEffect(() => {
    if (!open) return;
    const entry = byProvider[provider];
    if (!entry?.data && !entry?.loading && !entry?.error) load(provider);
  }, [open, provider, byProvider, load]);

  useEffect(() => {
    setSearch('');
    setCustomOpen(false);
    setCustomId('');
  }, [provider]);

  const entry = byProvider[provider];
  const data = entry?.data;
  const currentModel = modelFor(provider);
  const defaultModel = defaultModelFor(provider);
  const isAssigned = assignedProvider === provider;

  const { visible, hiddenCount, cheapestId } = useMemo(() => {
    const models = data?.models ?? [];
    const query = foldText(search);
    let hidden = 0;

    const filtered = models.filter((model) => {
      if (query && !foldText(model.id).includes(query) && !foldText(model.name).includes(query)) {
        return false;
      }
      // Keep the model in use visible so the admin always sees what they have
      if (model.id === currentModel) return true;
      const hide =
        (!showAll && (!compatibility(model, useCase).compatible || model.status === 'deprecated')) ||
        (requireImage && model.capabilities.imageInput !== true) ||
        (requireReasoning && model.capabilities.reasoning !== true) ||
        (requireTools && model.capabilities.toolCalling !== true);
      if (hide) hidden++;
      return !hide;
    });

    const cost = (m: AIModelInfo) => estimateUseCost(m, useCase) ?? Number.POSITIVE_INFINITY;
    filtered.sort((a, b) => {
      switch (sort) {
        case 'cost':
          return cost(a) - cost(b) || a.name.localeCompare(b.name);
        case 'newest':
          return (b.releaseDate ?? '').localeCompare(a.releaseDate ?? '') || a.name.localeCompare(b.name);
        case 'context':
          return (b.contextWindow ?? 0) - (a.contextWindow ?? 0) || cost(a) - cost(b);
        default:
          return a.name.localeCompare(b.name);
      }
    });

    // On a price tie, credit the model in use (then the default) rather than an arbitrary one
    const rank = (m: AIModelInfo) => (m.id === currentModel ? 0 : m.id === defaultModel ? 1 : 2);
    const cheapest = models
      .filter(
        (m) =>
          compatibility(m, useCase).compatible && m.status !== 'deprecated' && estimateUseCost(m, useCase) !== null,
      )
      .sort((a, b) => cost(a) - cost(b) || rank(a) - rank(b))[0];

    return { visible: filtered, hiddenCount: hidden, cheapestId: cheapest?.id };
  }, [data, search, sort, useCase, currentModel, defaultModel, requireImage, requireReasoning, requireTools, showAll]);

  useEffect(() => {
    // Start on the model in use; otherwise nothing is highlighted until the admin moves
    setActiveIndex(isAssigned ? visible.findIndex((m) => m.id === currentModel) : -1);
  }, [visible, currentModel, isAssigned]);

  useEffect(() => {
    if (!scrollOnMove.current) return;
    scrollOnMove.current = false;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const choose = (model: AIModelInfo) => {
    if (!compatibility(model, useCase).compatible) return;
    onSelect(provider, model.id === defaultModel ? null : model.id, model);
  };

  const onListKeyDown = (event: React.KeyboardEvent) => {
    if (!visible.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      scrollOnMove.current = true;
      setActiveIndex((i) => Math.min(i + 1, visible.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      scrollOnMove.current = true;
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter' && visible[activeIndex]) {
      event.preventDefault();
      choose(visible[activeIndex]);
    }
  };

  const currentListed = !data || data.models.some((m) => m.id === currentModel);
  const customValid = MODEL_ID_PATTERN.test(customId.trim());
  const notes = (data?.notes ?? []).filter((note) => useCase === 'research' || !/search/i.test(note));
  const providerLabel = providers.find((p) => p.id === provider)?.name ?? provider;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      labelledBy="model-picker-title"
      initialFocusRef={searchRef}
      className="h-[92vh] sm:h-auto sm:max-h-[84vh] sm:max-w-[760px]"
    >
      {/* Header */}
      <div className="px-5 pt-5 sm:px-7 sm:pt-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[13px] text-neutral-400">{meta.shortLabel}</p>
            <h2 id="model-picker-title" className="mt-0.5 text-[22px] font-normal tracking-[-0.02em] text-ink">
              Choose a model
            </h2>
          </div>
          <IconButton label="Close" onClick={onClose} className="-mr-1 bg-neutral-100 text-neutral-600">
            <X className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
        </div>

        {providers.length > 1 && (
          <SegmentedControl
            label="Provider"
            className="mt-4"
            value={provider}
            onChange={setProvider}
            items={providers.map((p) => ({
              value: p.id,
              label: (
                <>
                  {p.name}
                  {assignedProvider === p.id && (
                    <span className="h-1.5 w-1.5 rounded-full bg-primary-600" aria-label="in use" />
                  )}
                </>
              ),
            }))}
          />
        )}

        {/* Search */}
        <div className="mt-4 flex items-center gap-3 border-b border-neutral-200/80 pb-3">
          <Search className="h-[18px] w-[18px] flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
          <input
            ref={searchRef}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onListKeyDown}
            placeholder={`Search ${providerLabel} models`}
            aria-label="Search models"
            aria-controls="model-list"
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[16px] text-ink sm:text-[15px] placeholder:text-neutral-400 focus:outline-none focus:ring-0"
          />
          <IconButton
            label="Refresh models and prices"
            size="sm"
            onClick={() => load(provider, true)}
            disabled={entry?.loading}
          >
            <RefreshCw className={`h-4 w-4 ${entry?.loading ? 'animate-spin' : ''}`} strokeWidth={1.75} />
          </IconButton>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between gap-2 py-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <Toggle active={requireImage} onClick={() => setRequireImage((v) => !v)} icon={ImageIcon} label="Vision" />
            <Toggle active={requireReasoning} onClick={() => setRequireReasoning((v) => !v)} icon={Brain} label="Reasoning" />
            <Toggle active={requireTools} onClick={() => setRequireTools((v) => !v)} icon={Wrench} label="Tools" />
            <Toggle
              active={showAll}
              onClick={() => setShowAll((v) => !v)}
              label="Show all"
              title="Include deprecated and unsuitable models"
            />
          </div>
          <SegmentedControl label="Sort" value={sort} onChange={setSort} items={SORTS} />
        </div>
      </div>

      {/* Status */}
      <div className="space-y-1.5 px-5 sm:px-7">
        {entry?.error && (
          <Notice tone="red">
            {entry.error}{' '}
            <button type="button" onClick={() => load(provider, true)} className="font-medium underline underline-offset-2">
              Retry
            </button>
          </Notice>
        )}
        {data?.liveList.status === 'error' && (
          <Notice tone="amber">Couldn't list models for this key. Showing the catalog.</Notice>
        )}
        {data?.catalog.status === 'unavailable' && <Notice tone="amber">Prices unavailable right now.</Notice>}
        {data?.catalog.status === 'stale' && <Notice tone="amber">Prices may be out of date.</Notice>}
        {!currentListed && (
          <Notice tone="amber">
            <span className="font-ui-mono">{currentModel}</span> isn't listed for this key.
          </Notice>
        )}
        {notes.map((note) => (
          <Notice key={note} tone="neutral">
            {note}
          </Notice>
        ))}
      </div>

      {/* List */}
      <div className="min-h-[200px] flex-1 overflow-y-auto px-2.5 pb-2 sm:px-4">
        {entry?.loading && !data ? (
          <div className="space-y-1 px-2.5 pt-2" aria-busy="true" aria-label="Loading models">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[58px] animate-pulse rounded-2xl bg-neutral-100/80" />
            ))}
          </div>
        ) : data && visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <p className="text-sm text-neutral-500">No models match</p>
            {hiddenCount > 0 && !showAll && (
              <Button size="sm" variant="ghost" onClick={() => setShowAll(true)}>
                Show {hiddenCount} hidden
              </Button>
            )}
          </div>
        ) : (
          <>
          <div className="sticky top-0 z-10 hidden grid-cols-[20px_minmax(0,1fr)_auto_112px_72px] gap-x-3 bg-white/95 px-3 pb-1.5 pt-2 text-[11px] text-neutral-400 backdrop-blur sm:grid">
            <span />
            <span>{visible.length} models</span>
            <span />
            <span className="text-right">In / out · 1M</span>
            <span className="text-right">Per {meta.estimate.unit}</span>
          </div>
          <ul
            id="model-list"
            ref={listRef}
            role="listbox"
            aria-label="Models"
            tabIndex={-1}
            onKeyDown={onListKeyDown}
            className="space-y-0.5 pt-1"
          >
            {visible.map((model, index) => (
              <ModelRow
                key={model.id}
                index={index}
                model={model}
                useCase={useCase}
                active={index === activeIndex}
                selected={isAssigned && model.id === currentModel}
                isDefault={model.id === defaultModel}
                isCheapest={model.id === cheapestId}
                onHover={() => setActiveIndex(index)}
                onChoose={() => choose(model)}
              />
            ))}
            {hiddenCount > 0 && !showAll && (
              <li className="px-3 pb-1 pt-3 text-center">
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="text-xs text-neutral-400 transition-colors hover:text-ink"
                >
                  {hiddenCount} hidden
                </button>
              </li>
            )}
          </ul>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex min-h-[60px] items-center justify-between gap-3 border-t border-neutral-200/70 bg-neutral-50/60 px-5 py-3 sm:px-7">
        {customOpen ? (
          <form
            className="flex w-full items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (customValid) onSelect(provider, customId.trim(), null);
            }}
          >
            <input
              autoFocus
              value={customId}
              onChange={(e) => setCustomId(e.target.value)}
              placeholder="exact-model-id"
              aria-label="Custom model ID"
              aria-invalid={customId.length > 0 && !customValid}
              className="h-9 min-w-0 flex-1 rounded-full border-0 bg-white px-4 font-ui-mono text-[16px] text-ink sm:text-[13px] ring-1 ring-inset ring-neutral-200 placeholder:text-neutral-400 focus:ring-2 focus:ring-primary-600/40"
            />
            <Button size="sm" variant="dark" type="submit" disabled={!customValid}>
              Use
            </Button>
            <IconButton label="Cancel" size="sm" onClick={() => setCustomOpen(false)}>
              <X className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          </form>
        ) : (
          <>
            <p className="flex min-w-0 items-center gap-1.5 truncate text-xs text-neutral-400">
              <span className="truncate">
                Per 1M tokens ·{' '}
                <a
                  href={data?.catalog.sourceUrl ?? 'https://models.dev'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-ink"
                >
                  models.dev
                </a>
                {data?.catalog.fetchedAt && ` · ${formatRelativeTime(data.catalog.fetchedAt)}`}
              </span>
              {data?.pricingUrl && (
                <a
                  href={data.pricingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex flex-shrink-0 items-center gap-0.5 hover:text-ink"
                >
                  {providerLabel} pricing
                  <ArrowUpRight className="h-3 w-3" strokeWidth={1.75} />
                </a>
              )}
            </p>
            <div className="flex flex-shrink-0 items-center gap-3">
              <span className="hidden items-center gap-1 text-xs text-neutral-400 sm:inline-flex">
                <CornerDownLeft className="h-3 w-3" strokeWidth={1.75} /> select
              </span>
              <Button size="sm" variant="secondary" onClick={() => setCustomOpen(true)}>
                Custom ID
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

const Toggle: React.FC<{
  active: boolean;
  onClick: () => void;
  label: string;
  title?: string;
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}> = ({ active, onClick, label, title, icon: Icon }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    title={title}
    className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
      active ? 'bg-ink text-white' : 'text-neutral-500 ring-1 ring-inset ring-neutral-200 hover:text-ink'
    }`}
  >
    {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />}
    {label}
  </button>
);

const Notice: React.FC<{ tone: 'red' | 'amber' | 'neutral'; children: React.ReactNode }> = ({ tone, children }) => (
  <p
    className={`flex items-start gap-2 text-xs ${
      tone === 'red' ? 'text-primary-700' : tone === 'amber' ? 'text-amber-700' : 'text-neutral-400'
    }`}
  >
    {tone !== 'neutral' && <CircleAlert className="mt-px h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />}
    <span className="min-w-0">{children}</span>
  </p>
);

const CAPABILITIES: Array<{
  key: keyof AIModelInfo['capabilities'];
  label: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}> = [
  { key: 'imageInput', label: 'Reads images', icon: ImageIcon },
  { key: 'pdfInput', label: 'Reads PDFs', icon: FileText },
  { key: 'audioInput', label: 'Audio input', icon: Mic },
  { key: 'videoInput', label: 'Video input', icon: Video },
  { key: 'reasoning', label: 'Reasoning', icon: Brain },
  { key: 'toolCalling', label: 'Tool calling', icon: Wrench },
];

const ModelRow: React.FC<{
  index: number;
  model: AIModelInfo;
  useCase: AIUseCase;
  active: boolean;
  selected: boolean;
  isDefault: boolean;
  isCheapest: boolean;
  onHover: () => void;
  onChoose: () => void;
}> = ({ index, model, useCase, active, selected, isDefault, isCheapest, onHover, onChoose }) => {
  const { compatible, reason } = compatibility(model, useCase);
  const cost = estimateUseCost(model, useCase);
  const unit = USE_CASE_META[useCase].estimate.unit;
  const caps = CAPABILITIES.filter(({ key }) => model.capabilities[key] === true);

  return (
    <li
      data-index={index}
      role="option"
      aria-selected={selected}
      aria-disabled={!compatible}
      title={reason}
      onMouseEnter={onHover}
      onClick={onChoose}
      className={`group grid cursor-pointer grid-cols-[20px_minmax(0,1fr)] items-center gap-x-3 rounded-2xl px-3 py-2.5 transition-colors sm:grid-cols-[20px_minmax(0,1fr)_auto_112px_72px] ${
        active ? 'bg-neutral-100/80' : ''
      } ${compatible ? '' : 'cursor-not-allowed opacity-40'}`}
    >
      <span
        className={`flex h-[18px] w-[18px] items-center justify-center rounded-full transition-colors ${
          selected ? 'bg-primary-600 text-white' : 'ring-1 ring-inset ring-neutral-300 group-hover:ring-neutral-400'
        }`}
        aria-hidden="true"
      >
        {selected && <Check className="h-3 w-3" strokeWidth={2.5} />}
      </span>

      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-[14px] text-ink">{model.name}</span>
          {isCheapest && <Pill tone="green">Cheapest</Pill>}
          {isDefault && <Pill tone="outline">Default</Pill>}
          {model.status === 'deprecated' && <Pill tone="red">Deprecated</Pill>}
          {(model.status === 'preview' || model.status === 'beta') && (
            <Pill tone="amber">{model.status === 'beta' ? 'Beta' : 'Preview'}</Pill>
          )}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-neutral-400">
          <span className="truncate font-ui-mono text-[11.5px]">{model.id}</span>
          <span className="hidden flex-shrink-0 sm:inline">{formatTokens(model.contextWindow)}</span>
        </div>
      </div>

      <div className="hidden items-center gap-1 text-neutral-400 sm:flex">
        {caps.map(({ key, label, icon: Icon }) => (
          <span key={key} title={label} aria-label={label} className="flex h-6 w-6 items-center justify-center">
            <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
          </span>
        ))}
      </div>

      <div
        className="col-start-2 mt-1 text-xs tabular-nums text-neutral-500 sm:col-start-auto sm:mt-0 sm:text-right"
        title={model.pricing?.tiered ? 'Higher rates apply to very long prompts' : 'Input / output per 1M tokens'}
      >
        {model.pricing ? (
          <>
            {formatPrice(model.pricing.input)} <span className="text-neutral-300">/</span> {formatPrice(model.pricing.output)}
            {model.pricing.tiered && '*'}
          </>
        ) : (
          <span className="text-neutral-300">No price</span>
        )}
        <span className="text-neutral-400 sm:hidden"> · {formatCost(cost)} per {unit}</span>
      </div>

      <div className="hidden text-right text-[13px] tabular-nums text-ink sm:block" title={`Estimated per ${unit}`}>
        {formatCost(cost)}
      </div>
    </li>
  );
};

